import React, { useEffect, useRef, useState } from "react";
import { Button, message, Upload, Table, Space } from "antd";
import { UploadOutlined, DeleteOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { HttpMethod, initFetch } from "../../utils/fetcher";

export default function MaillerUpload({ mails, document, fileList, onChange, onLoadingChange }) {
	const [messageApi, contextHolder] = message.useMessage();
	const isControlled = Array.isArray(fileList) && typeof onChange === "function";
	const [internalFileList, setInternalFileList] = useState([]);
	const effectiveFileList = isControlled ? fileList : internalFileList;

	const fileListRef = useRef(effectiveFileList);
	useEffect(() => { fileListRef.current = effectiveFileList; }, [effectiveFileList]);

	const setFileListSafe = (updater) => {
		const base = fileListRef.current || [];
		const next = typeof updater === "function" ? updater(base) : updater;
		fileListRef.current = next;

		if (isControlled) onChange(next);
		else setInternalFileList(next);
	};

	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (typeof onLoadingChange === "function") {
			onLoadingChange(loading);
		}
	}, [loading, onLoadingChange]);

	useEffect(() => {
		if (document) {
			setFileListSafe(prev => {
				const exists = prev.find(f => f.uid === document.uid);
				if (!exists) {
					return [...prev, document];
				}
				return prev;
			});
		}
		//console.log(document);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [document]);

	useEffect(() => {
		setFileListSafe([]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mails]); // reset quand mails change (comme ton comportement actuel)

	const customRequest = async ({ file, onSuccess, onError }) => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("nom", file.name);

		try {
			setLoading(true);

			const { url, params } = initFetch(`uploadPieceJointe`, HttpMethod.POST, {
				Authorization: true,
				formData: true,
				pdf: false,
			});

			const response = await fetch(url, { ...params, body: formData });
			if (!response.ok) throw new Error("Upload failed");

			const data = await response.json();

			setFileListSafe(prev =>
				prev.map(f =>
					f.uid === file.uid
						? { ...f, status: "done", url: data.url, serverId: data.id }
						: f
				)
			);

			messageApi.success(`${file.name} uploadé avec succès`);
			onSuccess(data, file);
		} catch (error) {
			setFileListSafe(prev => prev.map(f => (f.uid === file.uid ? { ...f, status: "error" } : f)));
			messageApi.error(`Échec de l'upload de ${file.name}`);
			onError(error);
		} finally {
			setLoading(false);
		}
	};

	const removeByUid = (uid) => {
		setFileListSafe(prev => prev.filter(f => f.uid !== uid));
		messageApi.success("Fichier supprimé");
	};

	const columns = [
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 20,
			render: (status) => status === "done"
				? <CheckOutlined style={{ color: "green" }} />
				: <CloseOutlined style={{ color: "red" }} />,
		},
		{ title: "Nom du fichier", dataIndex: "name", key: "name", ellipsis: true },
		{
			title: "Taille",
			dataIndex: "size",
			key: "size",
			width: 120,
			align: "right",
			render: (size) => size ? `${(size / 1024).toFixed(2)} KB` : '-',
		},
		{
			title: "",
			key: "actions",
			width: 80,
			align: "right",
			render: (_, record) => (
				<Space>
					<Button
						size="small"
						type="link"
						danger
						icon={<DeleteOutlined />}
						onClick={() => removeByUid(record.uid)}
					/>
				</Space>
			),
		},
	];

	const uploadProps = {
		accept: ".pdf",
		multiple: true,
		customRequest,
		fileList: effectiveFileList, // Upload contrôlé
		onChange: ({ fileList: next }) => setFileListSafe(next),
		onRemove: (file) => {
			removeByUid(file.uid);
			return true;
		},
		showUploadList: false,
		beforeUpload: (file) => {
			const isPDF = file.type === "application/pdf";
			if (!isPDF) messageApi.error("Vous ne pouvez uploader que des fichiers PDF!");
			const isLt10M = file.size / 1024 / 1024 < 10;
			if (!isLt10M) messageApi.error("Le fichier doit faire moins de 10MB!");
			return isPDF && isLt10M;
		},
	};

	return (
		<>
			{contextHolder}
			{effectiveFileList.length > 0 && (
				<Table
					showHeader={false}
					columns={columns}
					dataSource={effectiveFileList}
					rowKey="uid"
					pagination={false}
					size="small"
				/>
			)}
			<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
			<Upload {...uploadProps}>
				<Button icon={<UploadOutlined />} loading={loading} size="large">
					Pièces jointes (PDF)
				</Button>
			</Upload>
			</div>
		</>
	);
}
